import { useState, useEffect } from 'react';
import { navLinks, appData, aboutData, contactData } from '@/mocks/homeData';
import HeroSection from './components/HeroSection';
import IndexSection from './components/IndexSection';
import CommentsSection from './components/CommentsSection';
import MediaSection from './components/MediaSection';
import CarouselSection from './components/CarouselSection';
import FaqSection from './components/FaqSection';
import RankingSection from './components/RankingSection';
import CardsSection from './components/CardsSection';
import UpdatesSection from './components/UpdatesSection';
import TopicsSection from './components/TopicsSection';
import FeaturesSection from './components/FeaturesSection';

export default function Home() {
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setHeaderScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          headerScrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-background-200'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-[72px]">
            <a href="#hero" className="flex items-center gap-2 cursor-pointer">
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-play-circle-fill text-white text-lg"></i>
              </div>
              <span className="text-lg md:text-xl font-bold text-foreground-900 font-heading">
                吃瓜视频
              </span>
            </a>

            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="whitespace-nowrap cursor-pointer px-3 py-2 text-sm font-medium text-foreground-600 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-all duration-150"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer text-foreground-700 hover:bg-background-100 transition-colors"
              aria-label="菜单"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white/98 backdrop-blur-md border-t border-background-200">
            <div className="px-4 py-3 flex flex-col gap-0.5">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={handleNavClick}
                  className="whitespace-nowrap cursor-pointer px-4 py-3 text-sm font-medium text-foreground-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </header>

      <main>
        {/* Hero */}
        <HeroSection />

        {/* #index - 热门专题合集 */}
        <IndexSection />

        {/* #app - 移动端入口 */}
        <section id="app" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-14 bg-background-50 border border-background-200 rounded-2xl p-8 md:p-12">
              <div className="flex-1 w-full lg:w-auto text-center lg:text-left">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  多端追剧
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {appData.title}
                </h2>
                <p className="text-base md:text-lg text-primary-600 font-medium mb-4">
                  {appData.subtitle}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  {appData.description}
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {appData.features.map((f) => (
                    <span
                      key={f}
                      className="text-xs md:text-sm px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 font-medium"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 w-48 md:w-56">
                <div className="relative w-full aspect-[9/16] rounded-2xl overflow-hidden border-4 border-background-300">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20smartphone%20mockup%20displaying%20Chinese%20drama%20streaming%20app%20interface%20with%20beautiful%20period%20drama%20thumbnail%20on%20screen%2C%20clean%20minimal%20UI%2C%20warm%20lighting%2C%20premium%20product%20photography%2C%20soft%20shadows%2C%20elegant%20presentation%20on%20neutral%20background&width=450&height=800&seq=app-mobile-mockup&orientation=portrait"
                    alt="吃瓜视频移动端 - 随时随地追剧"
                    title="吃瓜视频移动端高清播放"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* #comments - 追剧热评 */}
        <CommentsSection />

        {/* #media - 精彩剧集 */}
        <MediaSection />

        {/* #carousel - 焦点推荐 */}
        <CarouselSection />

        {/* #faq - 常见问题 */}
        <FaqSection />

        {/* #about - 关于吃瓜视频 */}
        <section id="about" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 text-center">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
              平台介绍
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-6">
              {aboutData.title}
            </h2>
            {aboutData.paragraphs.map((p, i) => (
              <p
                key={i}
                className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto mb-4 last:mb-0"
              >
                {p}
              </p>
            ))}
          </div>
        </section>

        {/* #ranking - 热播榜单 */}
        <RankingSection />

        {/* #cards - 剧集卡片 */}
        <CardsSection />

        {/* #updates - 最新动态 */}
        <UpdatesSection />

        {/* #contact - 联系我们 */}
        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[700px] mx-auto px-4 md:px-6">
            <div className="bg-background-50 border border-background-200 rounded-2xl p-8 md:p-12 text-center">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3">
                联系方式
              </span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
                {contactData.title}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8 max-w-lg mx-auto">
                {contactData.description}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
                <div className="flex items-center gap-2.5 px-5 py-3 rounded-xl bg-background-100 border border-background-200">
                  <i className="ri-mail-line text-lg text-primary-500"></i>
                  <span className="text-sm font-medium text-foreground-700">{contactData.email}</span>
                </div>
                <div className="flex items-center gap-2.5 px-5 py-3 rounded-xl bg-background-100 border border-background-200">
                  <i className="ri-wechat-line text-lg text-accent-500"></i>
                  <span className="text-sm font-medium text-foreground-700">{contactData.wechat}</span>
                </div>
                <div className="flex items-center gap-2.5 px-5 py-3 rounded-xl bg-background-100 border border-background-200">
                  <i className="ri-weibo-line text-lg text-primary-500"></i>
                  <span className="text-sm font-medium text-foreground-700">{contactData.weibo}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* #topics - 热门话题 */}
        <TopicsSection />

        {/* #features - 平台特色 */}
        <FeaturesSection />
      </main>

      {/* Footer */}
      <footer id="footer" className="bg-foreground-900 text-white">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                  <i className="ri-play-circle-fill text-white text-lg"></i>
                </div>
                <span className="text-lg font-bold font-heading">吃瓜视频</span>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">
                吃瓜视频在线观看完整版延禧攻略，支持延禧攻略等热门剧集完整版在线看。高清播放、免注册、免付费，追剧不等待。
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-white/90">快速导航</h4>
              <div className="flex flex-col gap-2.5">
                <a href="#media" className="text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap">精彩剧集</a>
                <a href="#ranking" className="text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap">热播榜单</a>
                <a href="#topics" className="text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap">热门话题</a>
                <a href="#carousel" className="text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap">焦点推荐</a>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-white/90">热门剧集</h4>
              <div className="flex flex-col gap-2.5">
                <a href="#media" className="text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap">延禧攻略全集</a>
                <a href="#media" className="text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap">庆余年</a>
                <a href="#media" className="text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap">甄嬛传</a>
                <a href="#media" className="text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap">琅琊榜</a>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-white/90">合作伙伴</h4>
              <p className="text-sm text-white/40">
                敬请期待
              </p>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-white/40">
              &copy; 2024 吃瓜视频. 热门剧集完整版高清在线播放平台.
            </p>
            <div className="flex items-center gap-4">
              <a href="#faq" className="text-xs text-white/40 hover:text-white/70 transition-colors whitespace-nowrap">常见问题</a>
              <a href="#contact" className="text-xs text-white/40 hover:text-white/70 transition-colors whitespace-nowrap">联系我们</a>
              <a href="#about" className="text-xs text-white/40 hover:text-white/70 transition-colors whitespace-nowrap">关于我们</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}